// The browser holds only the public key (REQ-SEC-02). Sessions live in sessionStorage so they
// end when the app is closed (REQ-SEC-01); the server also refuses sessions older than 12 hours.
import { createClient, type Session } from "@supabase/supabase-js";
import { env } from "./env";

const storage = typeof window !== "undefined" ? window.sessionStorage : undefined;
const SESSION_KEY = "rk-session";

export const supabase = createClient(env.supabaseUrl, env.supabaseKey, {
  auth: { storage, persistSession: true, autoRefreshToken: true, detectSessionInUrl: false, storageKey: SESSION_KEY }
});

function storedSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch {
    return null;
  }
}

/** The current session without waiting on a token refresh that may be stuck while signal comes and goes. */
export async function currentSession(): Promise<Session | null> {
  const timeout = new Promise<null>((resolve) => setTimeout(() => resolve(null), 2500));
  const live = supabase.auth.getSession().then((r) => r.data.session).catch(() => null);
  return (await Promise.race([live, timeout])) ?? storedSession();
}

export async function accessToken(): Promise<string | null> {
  return (await currentSession())?.access_token ?? null;
}

/**
 * Data API client. It takes the access token straight from the stored session, so reads never wait
 * on the sign-in client's token refresh (which can stall for half a minute while signal comes and goes).
 */
export const dataClient = createClient(env.supabaseUrl, env.supabaseKey, {
  accessToken: async () => storedSession()?.access_token ?? null,
  global: { fetch: (input, init) => fetchWithTimeout(input, init, 20_000) }
});

/** A request that gives up after a while: when signal drops mid-request, a fetch can otherwise hang forever. */
export function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}, ms = 30_000): Promise<Response> {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), ms);
  if (init.signal) init.signal.addEventListener("abort", () => ctrl.abort());
  return fetch(input, { ...init, signal: ctrl.signal }).finally(() => clearTimeout(t));
}
